import { useState, type FormEvent } from 'react';
import { contactChannels, navLinks } from '@/mocks/site';

const FORM_ID = 'dahro1boh653ivfvo480';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahro1boh653ivfvo480';

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

export default function ContactFooter() {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get('company_alt') || '').trim();
    if (honeypot !== '') {
      setStatus('success');
      return;
    }
    formData.delete('company_alt');

    setStatus('loading');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText) as typeof parsed;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const lowered = String(serverMsg).toLowerCase();
      const isSpam = lowered.includes('spam') || lowered.includes('form data is spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        formEl.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交未成功，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，暂时提交不了，请稍后再试。');
    }
  };

  return (
    <footer id="contact" className="w-full bg-secondary-950">
      <div className="mx-auto w-full max-w-6xl px-4 py-16 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <span className="text-xs font-medium tracking-[0.2em] text-secondary-300">CONTACT</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-background-50 md:text-4xl">爆料投稿与联系</h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-secondary-200 md:text-base">
              手里有可核实的线索，或者发现我们的时间线有偏差？通过下面的通道提交，我们会按优先级处理。
              请勿提交他人隐私信息或未经核实的指控。
            </p>

            <ul className="mt-8 flex flex-col gap-4">
              {contactChannels.map((channel) => (
                <li
                  key={channel.label}
                  className="flex items-start gap-4 rounded-lg border border-secondary-800 bg-secondary-900 p-4 md:p-5"
                >
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-secondary-800 text-secondary-100">
                    <i className={`${channel.icon} text-xl`} />
                  </span>
                  <div>
                    <p className="text-xs text-secondary-300">{channel.label}</p>
                    <p className="mt-1 break-all text-sm font-medium text-background-50">{channel.value}</p>
                    <p className="mt-1 text-xs text-secondary-300">{channel.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-secondary-800 bg-background-50 p-5 md:p-6">
            <h3 className="font-heading text-lg font-bold text-foreground-950">提交线索或纠错</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              描述越具体，处理越快。建议附上大致时间、信源类型和关键截图说明。
            </p>

            <form id="tips-form" data-readdy-form className="mt-5 flex flex-col gap-4" onSubmit={handleSubmit}>
              <div className="flex flex-col gap-4 sm:flex-row">
                <label className="flex-1">
                  <span className="mb-1.5 block text-sm font-medium text-foreground-800">你的称呼</span>
                  <input
                    type="text"
                    name="nickname"
                    required
                    placeholder="怎么称呼你"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
                  />
                </label>
                <label className="flex-1">
                  <span className="mb-1.5 block text-sm font-medium text-foreground-800">邮箱</span>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="用于回复核实结果"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
                  />
                </label>
              </div>

              <label>
                <span className="mb-1.5 block text-sm font-medium text-foreground-800">线索类型</span>
                <select
                  name="type"
                  required
                  defaultValue="爆料线索"
                  className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                >
                  <option value="爆料线索">爆料线索</option>
                  <option value="内容纠错">内容纠错</option>
                  <option value="专题建议">专题建议</option>
                  <option value="合作咨询">合作咨询</option>
                </select>
              </label>

              <label>
                <span className="mb-1.5 block text-sm font-medium text-foreground-800">具体内容</span>
                <textarea
                  name="content"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请描述事件经过、可核实的时间与信源（500 字以内）"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
                />
              </label>

              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-hp-field"
              />

              <button
                type="submit"
                disabled={status === 'loading'}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <i className={status === 'loading' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} />
                {status === 'loading' ? '提交中…' : '提交线索'}
              </button>

              {status === 'success' && (
                <p className="flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2.5 text-sm text-secondary-800">
                  <i className="ri-check-line" />
                  已收到，我们会核实后处理，必要时通过邮箱回复你。
                </p>
              )}
              {status === 'error' && (
                <p className="flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-800">
                  <i className="ri-error-warning-line" />
                  {formError}
                </p>
              )}
              <p className="text-xs text-foreground-400">
                表单编号：{FORM_ID} · 我们不会将你的联系方式用于其他用途。
              </p>
            </form>
          </div>
        </div>
      </div>

      <div className="border-t border-secondary-800">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-10 md:flex-row md:items-start md:justify-between md:px-6">
          <div className="max-w-sm">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-radar-line text-lg" />
              </span>
              <span className="font-heading text-lg font-bold text-background-50">瓜田情报局</span>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-secondary-300">
              汇总网络热议事件的后续进展、反转与当事人发言，只做梳理与追踪，不生产未经核实的内容。
            </p>
            <p className="mt-4 text-xs leading-relaxed text-secondary-400">
              本站内容整理自公开信息，仅供参考，最终请以当事人及官方声明为准。
            </p>
          </div>

          <nav className="grid grid-cols-2 gap-x-10 gap-y-2 sm:grid-cols-2" aria-label="页脚导航">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="whitespace-nowrap py-1 text-sm text-secondary-200 transition-colors hover:text-background-50"
              >
                {link.label}
              </a>
            ))}
            <a
              href="#contact"
              className="whitespace-nowrap py-1 text-sm text-secondary-200 transition-colors hover:text-background-50"
            >
              爆料投稿
            </a>
          </nav>

          <div className="flex gap-3">
            <a
              href="#top"
              aria-label="回到顶部"
              className="flex h-10 w-10 items-center justify-center rounded-md border border-secondary-800 text-secondary-200 transition-colors hover:border-secondary-600 hover:text-background-50"
            >
              <i className="ri-arrow-up-line text-lg" />
            </a>
            <a
              href="#live"
              aria-label="打开实时追踪"
              className="flex h-10 w-10 items-center justify-center rounded-md border border-secondary-800 text-secondary-200 transition-colors hover:border-secondary-600 hover:text-background-50"
            >
              <i className="ri-flashlight-line text-lg" />
            </a>
            <a
              href="#faq"
              aria-label="查看常见问题"
              className="flex h-10 w-10 items-center justify-center rounded-md border border-secondary-800 text-secondary-200 transition-colors hover:border-secondary-600 hover:text-background-50"
            >
              <i className="ri-question-line text-lg" />
            </a>
          </div>
        </div>

        <div className="border-t border-secondary-800">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-2 px-4 py-5 text-xs text-secondary-400 sm:flex-row sm:items-center sm:justify-between md:px-6">
            <p>© {new Date().getFullYear()} 瓜田情报局 · 热点后续追踪</p>
            <p>关键词：黑料大瓜 · 后续进展 · 反转 · 最新回应 · 当事人发声 · 第二弹</p>
          </div>
        </div>
      </div>
    </footer>
  );
}